import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './industry.reducer';

export const Industry = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const industryList = useAppSelector(state => state.industry.entities);
  const loading = useAppSelector(state => state.industry.loading);
  const totalItems = useAppSelector(state => state.industry.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="industry-heading" data-cy="IndustryHeading">
        <Translate contentKey="apiLocationApp.industry.home.title">Industries</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.industry.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/industry/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.industry.home.createLabel">Create new Industry</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {industryList && industryList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.industry.id">ID</Translate> <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.industry.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.industry.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.industry.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.industry.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllUsualResidents')}>
                  <Translate contentKey="apiLocationApp.industry.totalAllUsualResidents">Total All Usual Residents</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllUsualResidents')} />
                </th>
                <th className="hand" onClick={sort('agricultureForestryFishing')}>
                  <Translate contentKey="apiLocationApp.industry.agricultureForestryFishing">Agriculture Forestry Fishing</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('agricultureForestryFishing')} />
                </th>
                <th className="hand" onClick={sort('miningQuarrying')}>
                  <Translate contentKey="apiLocationApp.industry.miningQuarrying">Mining Quarrying</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('miningQuarrying')} />
                </th>
                <th className="hand" onClick={sort('manufacturing')}>
                  <Translate contentKey="apiLocationApp.industry.manufacturing">Manufacturing</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('manufacturing')} />
                </th>
                <th className="hand" onClick={sort('electricityGasWater')}>
                  <Translate contentKey="apiLocationApp.industry.electricityGasWater">Electricity Gas Water</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('electricityGasWater')} />
                </th>
                <th className="hand" onClick={sort('construction')}>
                  <Translate contentKey="apiLocationApp.industry.construction">Construction</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('construction')} />
                </th>
                <th className="hand" onClick={sort('wholesaleRetailTrade')}>
                  <Translate contentKey="apiLocationApp.industry.wholesaleRetailTrade">Wholesale Retail Trade</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('wholesaleRetailTrade')} />
                </th>
                <th className="hand" onClick={sort('transportationStorage')}>
                  <Translate contentKey="apiLocationApp.industry.transportationStorage">Transportation Storage</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('transportationStorage')} />
                </th>
                <th className="hand" onClick={sort('accommodationFoodService')}>
                  <Translate contentKey="apiLocationApp.industry.accommodationFoodService">Accommodation Food Service</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('accommodationFoodService')} />
                </th>
                <th className="hand" onClick={sort('informationCommunication')}>
                  <Translate contentKey="apiLocationApp.industry.informationCommunication">Information Communication</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('informationCommunication')} />
                </th>
                <th className="hand" onClick={sort('financialInsurance')}>
                  <Translate contentKey="apiLocationApp.industry.financialInsurance">Financial Insurance</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('financialInsurance')} />
                </th>
                <th className="hand" onClick={sort('realEstate')}>
                  <Translate contentKey="apiLocationApp.industry.realEstate">Real Estate</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('realEstate')} />
                </th>
                <th className="hand" onClick={sort('professionalScientific')}>
                  <Translate contentKey="apiLocationApp.industry.professionalScientific">Professional Scientific</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('professionalScientific')} />
                </th>
                <th className="hand" onClick={sort('administrativeSupport')}>
                  <Translate contentKey="apiLocationApp.industry.administrativeSupport">Administrative Support</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('administrativeSupport')} />
                </th>
                <th className="hand" onClick={sort('publicAdministration')}>
                  <Translate contentKey="apiLocationApp.industry.publicAdministration">Public Administration</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('publicAdministration')} />
                </th>
                <th className="hand" onClick={sort('education')}>
                  <Translate contentKey="apiLocationApp.industry.education">Education</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('education')} />
                </th>
                <th className="hand" onClick={sort('humanHealthSocialWork')}>
                  <Translate contentKey="apiLocationApp.industry.humanHealthSocialWork">Human Health Social Work</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('humanHealthSocialWork')} />
                </th>
                <th className="hand" onClick={sort('artsEntertainment')}>
                  <Translate contentKey="apiLocationApp.industry.artsEntertainment">Arts Entertainment</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('artsEntertainment')} />
                </th>
                <th className="hand" onClick={sort('otherServiceActivities')}>
                  <Translate contentKey="apiLocationApp.industry.otherServiceActivities">Other Service Activities</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('otherServiceActivities')} />
                </th>
                <th className="hand" onClick={sort('householdEmployers')}>
                  <Translate contentKey="apiLocationApp.industry.householdEmployers">Household Employers</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('householdEmployers')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {industryList.map((industry, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/industry/${industry.id}`} color="link" size="sm">
                      {industry.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${industry.geographicLevel}`} />
                  </td>
                  <td>{industry.date}</td>
                  <td>{industry.geography}</td>
                  <td>{industry.geographyCode}</td>
                  <td>{industry.totalAllUsualResidents}</td>
                  <td>{industry.agricultureForestryFishing}</td>
                  <td>{industry.miningQuarrying}</td>
                  <td>{industry.manufacturing}</td>
                  <td>{industry.electricityGasWater}</td>
                  <td>{industry.construction}</td>
                  <td>{industry.wholesaleRetailTrade}</td>
                  <td>{industry.transportationStorage}</td>
                  <td>{industry.accommodationFoodService}</td>
                  <td>{industry.informationCommunication}</td>
                  <td>{industry.financialInsurance}</td>
                  <td>{industry.realEstate}</td>
                  <td>{industry.professionalScientific}</td>
                  <td>{industry.administrativeSupport}</td>
                  <td>{industry.publicAdministration}</td>
                  <td>{industry.education}</td>
                  <td>{industry.humanHealthSocialWork}</td>
                  <td>{industry.artsEntertainment}</td>
                  <td>{industry.otherServiceActivities}</td>
                  <td>{industry.householdEmployers}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/industry/${industry.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/industry/${industry.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/industry/${industry.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.industry.home.notFound">No Industries found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={industryList && industryList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Industry;
